import { createContext, useContext, useState, useRef } from 'react';

// Holds AI Dispatch chat state at the app root so it:
//   - persists across client-side route navigation (React Router
//     keeps this provider mounted as the user navigates between
//     /, /admin, /active-load, etc.)
//   - resets on a full browser refresh (the provider re-instantiates
//     and all useState defaults reload)
//
// Replaces the localStorage-backed `dispatch_chat_${email}` persistence
// that previously kept messages alive across browser sessions. Chat
// memory is now session-scoped, matching user expectations: refresh
// the browser, get a clean slate; click around the app, keep your
// conversation.
//
// The draggable button POSITION still persists to localStorage (see
// AIDispatchChat) -- that's a UI ergonomic preference, not chat memory,
// and surviving refresh there is the desired behaviour.

const AIDispatchContext = createContext(null);

export const AIDispatchProvider = ({ children }) => {
  const [open, setOpen]                   = useState(false);
  const [messages, setMessages]           = useState([]);
  const [systemPrompt, setSystemPrompt]   = useState('');
  const [contextLoaded, setContextLoaded] = useState(false);
  const [availableLoads, setAvailableLoads] = useState([]);

  // Floating button position. ref (not state) because the component
  // mutates it imperatively during drag without re-rendering. The
  // initial position is read from localStorage in AIDispatchChat's
  // mount effect; the ref lives here so it persists across navigation
  // (a re-mount of the chat component during route change would
  // otherwise reset the position to the lower-right default).
  const btnPos = useRef({
    x: typeof window !== 'undefined' ? window.innerWidth - 80 : 0,
    y: typeof window !== 'undefined' ? window.innerHeight - 80 : 0,
  });

  return (
    <AIDispatchContext.Provider value={{
      open,           setOpen,
      messages,       setMessages,
      systemPrompt,   setSystemPrompt,
      contextLoaded,  setContextLoaded,
      availableLoads, setAvailableLoads,
      btnPos,
    }}>
      {children}
    </AIDispatchContext.Provider>
  );
};

export const useAIDispatch = () => {
  const ctx = useContext(AIDispatchContext);
  if (!ctx) throw new Error('useAIDispatch must be used within AIDispatchProvider');
  return ctx;
};
